/**
 * Render metadata embedded in an exported PSD's XMP packet — the symmetry
 * info (brief 03-procreate.md/00-WORK.md §2) needed to render a wallpaper
 * from re-imported painted artwork with no other app state available. Pure,
 * no DOM.
 *
 * Colour *values* are deliberately not carried here: a colour-tiled region
 * already exports one paint layer per colour instance for the artist to
 * paint separately (see psdExport.ts), so only the instance COUNT is needed
 * to reassemble them — never a stored/recalled hex palette.
 */
import {
  WALLPAPER_GROUPS,
  colourCountsFor,
  type ArtistTile,
  type LatticeParams,
  type WallpaperGroupId,
} from '@/patterns/wallpaper';

export interface PsdRenderMetadata {
  v: 1;
  wpgId: WallpaperGroupId;
  latticeParams: LatticeParams;
  artistTile: ArtistTile;
  /** colour-instance count only — never the actual hex values */
  colourCount: number;
}

const ARTIST_TILES = new Set<ArtistTile>(['standard', 'hexagon', 'rhombus', 'parallelogram']);

/** Namespace/attribute the payload is stashed under inside the XMP packet. */
const NS = 'escherllate';
const ATTR_RE = /escherllate:render="([^"]*)"/;

/**
 * Wrap the metadata JSON in a minimal, valid XMP packet rather than storing
 * bare JSON — the field is consumed by Photoshop/Procreate specifically as
 * XMP, so a well-formed envelope is more likely to survive a Procreate
 * re-export than arbitrary non-XML text would.
 */
export function serializeRenderMetadata(meta: PsdRenderMetadata): string {
  const json = JSON.stringify(meta).replace(/"/g, '&quot;');
  return (
    `<x:xmpmeta xmlns:x="adobe:ns:meta/">` +
    `<rdf:RDF xmlns:rdf="http://www.w3.org/1999/02/22-rdf-syntax-ns#">` +
    `<rdf:Description xmlns:${NS}="https://escherllate.app/ns/" ${NS}:render="${json}"/>` +
    `</rdf:RDF></x:xmpmeta>`
  );
}

/**
 * Distinguishes "no Escherllate metadata at all" (a foreign/Procreate-stripped
 * PSD — the user should re-export) from "metadata present but a specific field
 * is wrong" (a corrupt or version-mismatched packet), so the caller can show
 * the user a message that actually points at the problem rather than a single
 * catch-all. `absent` is the "no metadata" case; `error` names the bad field.
 */
export type RenderMetadataResult =
  | { meta: PsdRenderMetadata }
  | { absent: true }
  | { error: string };

/**
 * Parse + validate a render-metadata packet with a specific reason on failure.
 * Never a silent guess at the symmetry to render — anything wrong is reported.
 */
export function validateRenderMetadata(xmp: string | undefined): RenderMetadataResult {
  if (!xmp) return { absent: true };
  const match = ATTR_RE.exec(xmp);
  // A packet with neither our attribute nor parseable bare JSON is "no
  // Escherllate metadata", not a corrupt one — treat it as absent.
  const raw = match ? match[1]!.replace(/&quot;/g, '"') : xmp; // tolerate a bare-JSON packet too

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return match ? { error: 'metadata packet is not valid JSON' } : { absent: true };
  }
  if (!parsed || typeof parsed !== 'object') return { error: 'metadata is not an object' };
  const meta = parsed as Record<string, unknown>;

  if (meta.v !== 1) return { error: `unsupported metadata version (${String(meta.v)})` };
  if (typeof meta.wpgId !== 'string' || !(meta.wpgId in WALLPAPER_GROUPS)) {
    return { error: `unknown wallpaper group (${String(meta.wpgId)})` };
  }
  const wpgId = meta.wpgId as WallpaperGroupId;

  if (typeof meta.artistTile !== 'string' || !ARTIST_TILES.has(meta.artistTile as ArtistTile)) {
    return { error: `unknown artist tile (${String(meta.artistTile)})` };
  }
  if (typeof meta.colourCount !== 'number' || !Number.isInteger(meta.colourCount)) {
    return { error: 'colour count is not an integer' };
  }
  if (!colourCountsFor(wpgId).includes(meta.colourCount)) {
    return { error: `colour count ${meta.colourCount} is not valid for ${wpgId}` };
  }
  if (typeof meta.latticeParams !== 'object' || meta.latticeParams === null) {
    return { error: 'lattice parameters are missing' };
  }

  return {
    meta: {
      v: 1,
      wpgId,
      latticeParams: meta.latticeParams as LatticeParams,
      artistTile: meta.artistTile as ArtistTile,
      colourCount: meta.colourCount,
    },
  };
}

/**
 * Parse + validate a render-metadata packet. Returns null for anything
 * wrong — absent, malformed, unknown wallpaper group, an artist-tile value
 * outside the known set, or a colour count that isn't actually valid for
 * that wallpaper group — never a silent guess at the symmetry to render.
 * `validateRenderMetadata` is the finer-grained version used to tell the user
 * *why* a render failed.
 */
export function parseRenderMetadata(xmp: string | undefined): PsdRenderMetadata | null {
  const result = validateRenderMetadata(xmp);
  return 'meta' in result ? result.meta : null;
}
